<template>
  <SoDialog v-model="value" card-title="Stop Slot Offering Emails">
    <p class="mt-4">
      This takes the customer off the smart meter waitlist, so we stop emailing
      them when new appointment slots become available in their area.
    </p>

    <div class="mt-4">
      <ul class="ml-8" data-testid="stop-slot-offering-notes">
        <li>The customer can still book an installation at any time</li>
        <li>
          They can also register to the waitlist again if there are no slots or
          none work for them
        </li>
        <li>
          Smart meter installation campaign emails are separate from this and
          not affected
        </li>
      </ul>
    </div>

    <SoAlert v-if="isError" class="mt-5" type="error">
      Failed to remove the customer from the waitlist
    </SoAlert>

    <div class="d-flex gap-2 justify-end mt-8">
      <SoButton
        data-testid="stop-slot-offering-cancel"
        type="secondary"
        title="Cancel"
        large
        @click="value = false"
      />
      <SoButton
        :loading="isLoading"
        data-testid="stop-slot-offering-confirm"
        title="Stop slot offering emails"
        type="primary"
        large
        @click="removeFromWaitlist"
      />
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { useSnackbarStore } from "@/stores/snackbar"
  import { errorHandler, errorReporter } from "@/services/ErrorService"
  import { ImbpMutator } from "@/mutators/imbp/ImbpMutator"

  export default defineComponent({
    components: {
      SoDialog,
      SoAlert,
      SoButton,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["update:modelValue", "success"],
    data() {
      return {
        current: useCurrentAccountStore(),
        snackbar: useSnackbarStore(),
        removeInterestMutator: ImbpMutator.removeSmartMeterInterest(),
      }
    },
    computed: {
      value: {
        get(): boolean {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      isError(): boolean {
        return this.removeInterestMutator.isError
      },
      isLoading(): boolean {
        return this.removeInterestMutator.isPending
      },
    },
    methods: {
      removeFromWaitlist() {
        try {
          const billingAccountId = this.current.coreId.data
          if (!billingAccountId)
            throw new Error("Missing billing account for waitlist removal")

          this.removeInterestMutator
            .mutateAsync(billingAccountId.toString())
            .then(() => {
              this.current.imbpSmartMeterInterest.refetch()
              this.value = false
              this.snackbar.addSuccess(
                "Removed from the waitlist. We will stop sending slot offering emails.",
              )
              this.$emit("success")
            })
            .catch((error) => {
              errorReporter.report(error)
              this.snackbar.addError(
                errorHandler.userMessage(
                  error,
                  "Could not remove the customer from the waitlist",
                ),
              )
            })
        } catch (err) {
          errorReporter.report(err)
        }
      },
    },
  })
</script>
